// Field Labels page: #/assets/{site}/labels. Step 0 of a site, before Asset IDs and QR labels.
// Rev 2.8: MOCKUP_FIELD_LABELS_REV2.html (approved 2026-10-23). Words: FIELD_LABELS_WORDS.md sections 14 to 21.
//
// Left: You Are In (the room) and Equipment Type. Right: the room as grid paper, one piece of gear open at a time.
// Taps only. No keyboard. The gear is the record and every label is computed from it (field-labels-site.ts).
// Print Field Labels opens a preview: tap a wrong label to go to it, outlined red, and fix it there.
//
// Saved on this iPad per site, on every change, and works with no signal. Undo takes back the last change, up to 40.
// The walk also goes to the cloud the way the survey does (rule 36, COHESION_WORDS.md, Robert 2026-10-23 8:18 PM):
// on the iPad first, then the Outbox sends it on signal. Opened with signal, a newer walk from the cloud loads in.
// Nothing here makes an asset: the Asset ID Creator stays the only place that does.
// A site started on the PR #128/#129 notepad is carried over once, the first time it opens here.

import { dragActive, dragOrder } from "./drag-order";
import "./field-labels.css";
import type { SignInClaims } from "./badge";
import { DEFAULT_SIZES, fitOf, holdsOn, printFiles, sizeFromKey, sizeKey, sizeName, sizeOfLabel, TORO_MAX_SIZES, type LabelSize, type PrintFile } from "./field-labels";
import {
  addBreaker,
  breakerNames,
  circuitsOf,
  addGear,
  addRooms,
  addSubpanel,
  cleanText,
  clearBreaker,
  clearSection,
  endSectionAt,
  isBlank,
  setBlank,
  setCovers,
  toggleTandem,
  toggleShockOnly,
  toggleMain,
  setGridMain,
  notTheMain,
  unBlank,
  dropGear,
  fillTo,
  GEAR_TAG_MAX,
  isPanel,
  isSec,
  markPrinted,
  markSent,
  orderMoved,
  startPrintingOver,
  nextLetter,
  nextRoom,
  OUTSIDE,
  printStateOf,
  QUICK_INSIDE,
  QUICK_OUTSIDE,
  readSite,
  carryPhotos,
  photosBehind,
  recodeProblem,
  recodeRoom,
  recodeElRooms,
  removeGear,
  renameGear,
  renameProblem,
  ROOM_DESCRIPTION_MAX,
  roomLineup,
  roomLight,
  roomLetters,
  roomNames,
  setPanelSize,
  setPoles,
  setSplit,
  siteFromNotepad,
  subpanelOffer,
  siteLineup,
  toggleSpare,
  type FieldSite,
  type Gear,
  type RoomKindRev2,
  type RoomRename,
  type SiteLabel,
  markPushed,
  unmarkPushed,
  walkMarks,
  type SiteLineup,
  type SiteRoom,
  type WalkPhoto,
} from "./field-labels-site";
import { fieldLabelsPdf } from "./field-label-pdf";
import { readOffset } from "./printer-offset";
import { driverName, driverPage, PAGE_SIZES_SINCE_MS } from "./driver-page";
import { cardClick, cardFind, downloadFile as downloadCardFile, newPcState, pickedOf, printCard, rollStatus, sectionsOf, seedPicks, shareFile, shortDate, type PcRoll, type PcState } from "./print-card";
import { esc } from "./training-screens";
import { masterName, photoDriveName, sizePart } from "./drive-folders";
import { loadThumbs, resendPhoto, takePhoto } from "./walk-photos";
import { openCamera } from "./survey-camera";
import { assembleMaster } from "./master-pdf";
import { loadMasterState, retryMaster, sendMaster, stopMasterWatches } from "./master-send";
import { deviceId, saveRecord } from "./survey-sync";
import { fieldWalkPath, loadFieldWalk, withTimeLimit, type FieldWalkDoc } from "./firebase";
import { deviceList } from "./outbox";
import { isOnline } from "./offline";
import {
  blankMenuItems,
  circuitMenuItems,
  coverMenuItems,
  confirmSheet,
  fieldLabelsPage,
  labelMenuItems,
  leftHtml,
  menuHtml,
  light,
  PREVIEW_PX,
  panelPickSheet,
  problemLine,
  renameSheet,
  rightHtml,
  photoSheet,
  pushButton,
  type PushView,
  roomsSheet,
  roomWhere,
  sizesCard,
  squareMenuItems,
  typesSheet,
  type RightView,
} from "./field-labels-screens";
import { classesOf, WORKBOOK_CLASSES, type ClassOption } from "./asset-id";
import { bringIn, walkRowsOf } from "./bring-in";
import { qrEntries, qrPrintKey, qrRollOf } from "./qr-roll";
import { qrLabelsPdf } from "./qr-label-pdf";
import { readSitePack } from "./firebase";
import { heldName, lockedLost, onSheetGear, pushChanges, roomChanges, roomOnly, sheetOffWords } from "./sheet-lock";
import { waitingIds } from "./run-store";
import type { SitePack } from "./survey";

/** A paper-size block in the print preview: the size its labels take, and the size picked for this print. */
export interface PrintBlock extends PrintFile {
  print: LabelSize;
}

export interface FieldLabelsHost {
  root: HTMLElement;
  badge: SignInClaims;
  /** Draws the shared header, then `above` full width. */
  frame: (above: string, inner: string) => void;
  siteNumber: string;
  siteName: string;
  /** The site pack when the Assessment Sheet has it open: the rows already on the sheet. Else read from this iPad. */
  pack?: SitePack | null;
}

type Menu =
  | { kind: "sq"; gearId: string; sec: number; space: number; anchor: DOMRect }
  | { kind: "cc"; gearId: string; top: number; anchor: DOMRect }
  | { kind: "lab"; gearId: string; anchor: DOMRect }
  /** A Blank on a panel (F3a). */
  | { kind: "bl"; gearId: string; n: number; anchor: DOMRect }
  /** A section heading on sectioned gear: its Covers (decision 1). */
  | { kind: "sh"; gearId: string; sec: number; anchor: DOMRect };
type Sheet =
  | {
      /** The one print card, rev 5 (COHESION_CHANGE_LOG.md list 2, MOCKUP_ONE_PRINT_REV5.html). */
      kind: "preview";
      pc: PcState;
      /** Print At: a size for this print only, by the block's default size. The small file only; the master keeps the default. */
      over: Record<string, string>;
      /** Start Printing Over asked, waiting on Clear or Cancel. */
      startOver?: boolean;
    }
  /** sel: the room picked in the sheet. Done takes you there; a tap only picks it (Robert, 2026-10-23, 4:30 PM). */
  | { kind: "rooms"; sel: string | null }
  | { kind: "sizes" }
  | { kind: "types" }
  | { kind: "rmroom"; name: string }
  /** Tap PNL right after a panel: a new panel or a subpanel of it (decision 2). */
  | { kind: "panelpick"; parentId: string }
  /** The safe rename: the code stays, value is the part typed so far. */
  | { kind: "rename"; gearId: string; value: string }
  /** A room or gear photo full size, with Retake and Close (MOCKUP_ROOM_GEAR_PHOTO.html screen 4). gearId "": the room. */
  | { kind: "photo"; room: string; gearId: string };

interface State {
  site: string;
  company: string;
  data: FieldSite;
  /** The room you are in. */
  cur: string | null;
  /** The gear open on the right. */
  open: string | null;
  /** Copies of the record before each change, newest last. */
  history: string[];
  menu: Menu | null;
  sheet: Sheet | null;
  pick: { kind: RoomKindRev2; nums: Set<number>; part: string };
  flash: RightView["flash"];
  lineup: SiteLineup;
  /** One amber line after the EL re-code. Gone with the next change. */
  notice: string;
  /** One green line after a push: "ER1 pushed to the Assessment Sheet." (item 8). Gone with the next change. */
  good: string;
  /** Order mode on the right (FIELD_NAMING_B1_WORDS.md section 1). Not saved: it is a way of looking. */
  ordering: boolean;
  /** Remove tapped: the gear whose card asks Are You Sure? Cleared by any other redraw of the room. */
  rmAsk: string | null;
  /** Unlock tapped on On Sheet gear: its card asks Are You Sure? (ASSESSMENT_FLOW_B7_PART2_WORDS.md item 4). */
  unlockAsk: string | null;
  /** On Sheet gear unlocked for a rename or remove. Locks again when Field Labels opens from another page. */
  unlocked: Set<string>;
  /** The text box being typed in: one Undo step per box, however many letters. */
  typing: string;
  /**
   * The site as it was when Rooms opened, so Cancel puts it back (Robert, 2026-10-28, 5:15 AM). Steps counts the
   * changes made since. Null while Rooms is closed.
   */
  roomsStart: { rooms: string; cur: string | null; open: string | null; history: string[]; steps: number } | null;
  /** The room as it was when Order was tapped, so Cancel puts order and names back (B3). Null while not ordering. */
  orderStart: { rooms: string; open: string | null; history: string[]; steps: number } | null;
  /** In Rooms, a room tapped to change its code: the code picked so far (B3, Part B). Null: Add Rooms shows. */
  recode: { kind: "ER" | "EA"; num: number; part: string } | null;
}

export const UNDO_STEPS = 40;
export const storageKey = (company: string, site: string): string => `safetag.fieldLabels.v2.${company}.${site}`;
/** The PR #128/#129 notepad. Read once to carry a started site over. Never written again. */
export const notepadKey = (company: string, site: string): string => `safetag.fieldLabels.v1.${company}.${site}`;
/** The walk as it was before the EL re-code (2026-10-27). Written once, never read by the app. */
export const beforeEaKey = (company: string, site: string): string => `${storageKey(company, site)}.beforeEa`;

let host: FieldLabelsHost | null = null;
let st: State | null = null;
let wired = false;

let lastPrintRoom = "";
/** The last save to this iPad failed: a red line says so until one works. */
let saveFailed = false;

// ---- This iPad (wrapped: storage can be missing or refuse) ----

interface Saved {
  data: FieldSite;
  cur: string | null;
  /** EL rooms renamed EA on this load. Empty when there were none. */
  recoded: RoomRename[];
  /** The saved text before the re-code, kept once under beforeEaKey. */
  before: string;
}

/** The room you were in, under its new name after a re-code. */
const renamed = (cur: string | null, list: readonly RoomRename[]): string | null => list.find((x) => x.from === cur)?.to ?? cur;

export function loadSaved(get: (k: string) => string | null, company: string, site: string): Saved {
  try {
    const raw = get(storageKey(company, site));
    if (raw) {
      const v = JSON.parse(raw) as { rooms?: unknown; cur?: unknown };
      const data = readSite(v);
      const recoded = recodeElRooms(data);
      const was = renamed(typeof v.cur === "string" ? v.cur : null, recoded);
      const cur = was && data.rooms.some((r) => r.name === was) ? was : (data.rooms[0]?.name ?? null);
      return { data, cur, recoded, before: recoded.length ? raw : "" };
    }
    const old = get(notepadKey(company, site));
    if (old) {
      const v = JSON.parse(old) as { text?: unknown; choices?: unknown };
      const data = siteFromNotepad(typeof v.text === "string" ? v.text : "", v.choices && typeof v.choices === "object" ? (v.choices as Record<string, string>) : {});
      // The notepad stays where it is, so it is its own copy. No beforeEa needed.
      const recoded = recodeElRooms(data);
      return { data, cur: data.rooms[0]?.name ?? null, recoded, before: "" };
    }
  } catch {
    // A bad save reads as an empty site. The old notepad, if any, is still there.
  }
  return { data: { rooms: [] }, cur: null, recoded: [], before: "" };
}

function readStore(k: string): string | null {
  try {
    return window.localStorage.getItem(k);
  } catch {
    return null;
  }
}

/** "EL rooms are now EA: EL1 to EA1, EL4 to EA4. Flip a closed room to ER in Rooms." */
export function recodeNotice(list: readonly RoomRename[]): string {
  return list.length ? `EL rooms are now EA: ${list.map((x) => `${x.from} to ${x.to}`).join(", ")}. Flip a closed room to ER in Rooms.` : "";
}

/** Keeps the walk as it was before the re-code. Only the first copy is kept. */
function keepBeforeEa(company: string, site: string, text: string): void {
  if (!text) return;
  try {
    if (!window.localStorage.getItem(beforeEaKey(company, site))) window.localStorage.setItem(beforeEaKey(company, site), text);
  } catch {
    // No room for the copy. The cloud still holds the walk as it was until the Outbox sends the re-code.
  }
}

function writeSaved(): void {
  if (!st) return;
  try {
    window.localStorage.setItem(storageKey(st.company, st.site), JSON.stringify({ ...walkMarks(st.data), cur: st.cur, savedAt: new Date().toISOString() }));
    saveFailed = false;
    const el = host?.root.querySelector<HTMLElement>("#fl-saved");
    if (el) el.textContent = `Saved on this iPad ${new Date().toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}`;
  } catch {
    saveFailed = true;
  }
  queueWalkSoon();
  void sendRenamedPhotos();
}

// ---- The cloud copy (rule 36) ----

/** When this device last saved the walk, from its own copy. */
function localSavedAt(company: string, site: string): string {
  try {
    const raw = readStore(storageKey(company, site));
    return raw ? String((JSON.parse(raw) as { savedAt?: unknown }).savedAt ?? "") : "";
  } catch {
    return "";
  }
}

/** The walk as the cloud keeps it: counts the hub reads, and the walk itself as text. */
export function walkDoc(data: FieldSite, site: string, savedAt: string, who: { badgeId: string; holderName: string }, device: { id: string; name: string }): FieldWalkDoc {
  return {
    siteNumber: site,
    walkJson: JSON.stringify(walkMarks(data)),
    roomsTotal: data.rooms.length,
    roomsDone: data.rooms.filter((r) => r.done).length,
    gearTotal: data.rooms.reduce((n, r) => n + r.gear.length, 0),
    savedAt,
    badgeId: who.badgeId,
    holderName: who.holderName,
    deviceId: device.id,
    deviceName: device.name,
  };
}

/** Which copy wins when the page opens: the cloud's, only when it is newer and nothing here waits to send. */
export function cloudWins(cloud: Pick<FieldWalkDoc, "savedAt" | "walkJson"> | null, localAt: string, localRooms: number, waitingHere: boolean): boolean {
  if (!cloud || !cloud.walkJson || waitingHere) return false;
  return !localRooms || cloud.savedAt > localAt;
}

let walkTimer: ReturnType<typeof setTimeout> | null = null;

/** A burst of taps sends once. The iPad copy above is already saved. */
function queueWalkSoon(): void {
  if (walkTimer) clearTimeout(walkTimer);
  walkTimer = setTimeout(() => {
    walkTimer = null;
    void queueWalk();
  }, 1200);
}

async function queueWalk(): Promise<void> {
  if (!host || !st) return;
  const s = st;
  const savedAt = localSavedAt(s.company, s.site) || new Date().toISOString();
  let name = "";
  try {
    name = window.localStorage.getItem("st_survey_device") ?? "";
  } catch {
    name = "";
  }
  const data = walkDoc(s.data, s.site, savedAt, host.badge, { id: deviceId(), name: name || "This device" });
  await saveRecord({ path: fieldWalkPath(s.company, s.site), company: s.company, siteNumber: s.site, assetId: "Field Labels", card: "Walk", data: data as unknown as Record<string, unknown>, savedAt }).catch(() => undefined);
}

/** Opened with signal: a newer walk from another device loads in. A walk here that is newer goes up. */
async function syncWalk(company: string, site: string): Promise<void> {
  if (!isOnline()) return;
  let cloud: FieldWalkDoc | null;
  try {
    cloud = await withTimeLimit(loadFieldWalk(company, site), 15_000);
  } catch {
    return;
  }
  const path = fieldWalkPath(company, site);
  const waitingHere = (await deviceList.records().catch(() => [])).some((r) => r.path === path && r.state !== "sent");
  if (!st || st.site !== site || st.company !== company) return;
  const localAt = localSavedAt(company, site);
  if (cloudWins(cloud, localAt, st.data.rooms.length, waitingHere) && cloud) {
    let data: FieldSite;
    try {
      data = readSite(JSON.parse(cloud.walkJson));
    } catch {
      return;
    }
    // A cloud walk from a build before 2026-10-27 can still hold EL rooms. Same re-code, then it goes back up.
    const recoded = recodeElRooms(data);
    st.data = data;
    st.history = [];
    st.open = null;
    // A walk from another device replaces this one: Rooms closes, nothing here to cancel back to.
    if (st.sheet?.kind === "rooms" || st.sheet?.kind === "rmroom") st.sheet = null;
    st.roomsStart = null;
    st.cur = renamed(st.cur, recoded);
    if (!st.cur || !data.rooms.some((r) => r.name === st!.cur)) st.cur = data.rooms[0]?.name ?? null;
    if (recoded.length) {
      keepBeforeEa(company, site, JSON.stringify({ rooms: JSON.parse(cloud.walkJson).rooms, savedAt: cloud.savedAt }));
      st.notice = recodeNotice(recoded);
      writeSaved();
    } else
      try {
        window.localStorage.setItem(storageKey(company, site), JSON.stringify({ ...walkMarks(data), cur: st.cur, savedAt: cloud.savedAt }));
      } catch {
        // Not kept here. The cloud still holds it.
      }
    if (host && onThisPage()) {
      drawFieldLabels(host);
      const el = host.root.querySelector<HTMLElement>("#fl-saved");
      const when = new Date(cloud.savedAt).toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
      if (el) el.textContent = `Loaded from the cloud · ${cloud.deviceName || "another device"} ${when}`;
    }
    return;
  }
  if (st.data.rooms.length && !waitingHere && (!cloud || localAt > cloud.savedAt)) void queueWalk();
}

// ---- Drawing ----

function onThisPage(): boolean {
  return /^#\/assets\/[^/]+\/labels(\/|$)/.test(location.hash);
}

const room = (): SiteRoom | null => st?.data.rooms.find((r) => r.name === st!.cur) ?? null;
const gearById = (id: string): { r: SiteRoom; g: Gear } | null => {
  for (const r of st?.data.rooms ?? []) {
    const g = r.gear.find((x) => x.id === id);
    if (g) return { r, g };
  }
  return null;
};

/** A Field Labels text box has the focus. */
function typingHere(h: FieldLabelsHost): boolean {
  const a = document.activeElement as HTMLElement | null;
  return !!a && !!a.dataset?.flIn && h.root.contains(a);
}

/** A repaint held while typing. It runs a moment after you leave the box, so the tap that left it still lands. */
let repaintAfterTyping = false;

/** Opens the page, or repaints it whole. */
export function drawFieldLabels(h: FieldLabelsHost): void {
  host = h;
  wire();
  // A card held up mid-drag is never torn down by a repaint; the drop draws the page.
  if (dragActive() && st && st.site === h.siteNumber && st.company === h.badge.company) return;
  // Typing in a box here (Rename, the tag, the room description): an Outbox save elsewhere repaints the survey
  // section, which rebuilt the box and dropped the iPad keyboard after one letter (field test 2026-10-28 2:37 PM).
  // The repaint waits until you leave the box.
  if (typingHere(h) && st && st.site === h.siteNumber && st.company === h.badge.company) {
    repaintAfterTyping = true;
    return;
  }
  if (!st || st.site !== h.siteNumber || st.company !== h.badge.company) {
    const saved = loadSaved(readStore, h.badge.company, h.siteNumber);
    st = {
      site: h.siteNumber,
      company: h.badge.company,
      data: saved.data,
      cur: saved.cur,
      open: null,
      history: [],
      menu: null,
      sheet: null,
      pick: { kind: "ER", nums: new Set(), part: "" },
      flash: null,
      rmAsk: null,
      unlockAsk: null,
      unlocked: new Set(),
      lineup: siteLineup(saved.data),
      notice: recodeNotice(saved.recoded),
      good: "",
      ordering: false,
      typing: "",
      roomsStart: null,
      orderStart: null,
      recode: null,
    };
    // A carried-over notepad is saved in the new shape right away.
    if (saved.data.rooms.length && !readStore(storageKey(st.company, st.site))) writeSaved();
    // The EL re-code: the old walk kept once, then the new one saved here and sent by the Outbox.
    else if (saved.recoded.length) {
      keepBeforeEa(st.company, st.site, saved.before);
      writeSaved();
    }
    void syncWalk(st.company, st.site);
  }
  // The gate is read each time the page opens, so a mark made on the site page counts on the way back.
  if (!push || push.site !== st.site || push.company !== st.company || !h.root.querySelector("#fl-push")) {
    // Coming back from another page: a line left from before (Mark 00 first) is gone (field test 2026-10-29 8:34 AM).
    if (!h.root.querySelector("#fl-push")) {
      st.notice = "";
      st.good = takePushedLine(st.company, st.site);
      // Unlock lasts while Field Labels is open: back from the sheet (the push), every card locks again.
      st.unlocked.clear();
      st.unlockAsk = null;
    }
    void readPush(st.company, st.site);
  }
  st.lineup = siteLineup(st.data);
  // A repaint of the page already open (the Outbox save 1.2 s after a tap) keeps every panel where it was.
  // Walk item 6, FIELD_PHASE1_WORDS.md F1a. Coming from another page finds no panels here, so it opens at the top.
  const kept = SCROLLERS.map((sel) => h.root.querySelector<HTMLElement>(sel)?.scrollTop ?? 0);
  h.frame(fieldLabelsPage({ siteNumber: h.siteNumber, siteName: h.siteName, total: st.lineup.labels.length, push: pushView() }), "");
  const list = h.root.querySelector<HTMLElement>("#fl-right");
  if (list)
    dragOrder(list, {
      handle: ".fl-handle",
      scroller: () => host?.root.querySelector<HTMLElement>("#fl-right") ?? null,
      onDrop: (id, from, to) => {
        const r = room();
        if (st?.ordering && r && from !== to) change(() => dropGear(r, id, to));
        else drawParts();
      },
    });
  drawParts();
  SCROLLERS.forEach((sel, i) => {
    const el = h.root.querySelector<HTMLElement>(sel);
    if (el && kept[i]) el.scrollTop = kept[i]!;
  });
}

/** The parts of Field Labels that scroll on their own: both panels, and an open sheet or its type list. */
const SCROLLERS = ["#fl-left", "#fl-right", "#fl-overlay .ai-card", "#fl-overlay .fl-typelist"] as const;
/** The parts of a sheet that scroll. The card is what scrolls in Rooms, Label Sizes and More (checked 2026-10-27). */
const SHEET_SCROLLERS = ["#fl-overlay .ai-card", "#fl-overlay .fl-typelist"] as const;
/** The sheet drawn last, so a redraw of the same sheet keeps its scroll and a new one opens at the top. */
let lastSheet = "";

function drawParts(): void {
  if (!host || !st) return;
  const s = st;
  const root = host.root;
  s.lineup = siteLineup(s.data);
  const r = room();
  // Leaving Order any way but Cancel keeps what was done, the same as Done.
  if (!s.ordering) s.orderStart = null;
  const left = root.querySelector<HTMLElement>("#fl-left");
  if (left) left.innerHTML = leftHtml({ room: r, quick: s.cur === OUTSIDE ? QUICK_OUTSIDE : QUICK_INSIDE });
  const right = root.querySelector<HTMLElement>("#fl-right");
  if (right) {
    const y = right.scrollTop;
    right.innerHTML = rightHtml({ room: r, open: s.open, next: s.cur ? nextRoom(s.data, s.cur) : null, flash: s.flash, ordering: s.ordering, orderChanges: s.orderStart?.steps ?? 0, rmAsk: s.rmAsk, onSheet: onSheetHere(), unlocked: s.unlocked, unlockAsk: s.unlockAsk, pushedAt: r ? (s.data.pushed?.[r.name]?.at ?? "") : "" });
    right.scrollTop = y;
  }
  const line = root.querySelector<HTMLElement>("#fl-line");
  if (line)
    line.innerHTML = s.good && !saveFailed ? `<div class="ac-line green">${esc(s.good)}</div>` : problemLine([
      ...(saveFailed ? [{ tone: "red" as const, line: 0, text: "Not saved on this iPad. Storage is full or turned off. Make the PDFs before you leave this page." }] : []),
      ...(s.notice ? [{ tone: "amber" as const, line: 0, text: s.notice }] : []),
      ...s.lineup.problems,
    ]);
  const undo = root.querySelector<HTMLButtonElement>("#fl-undo");
  if (undo) undo.disabled = !s.history.length;
  const print = root.querySelector<HTMLButtonElement>("#fl-print");
  if (print) print.textContent = `Print Field Labels · ${s.lineup.labels.length}`;
  drawPush();
  drawOverlay();
  fitHeight();
  loadThumbs(root);
}

function fitHeight(): void {
  const main = host?.root.querySelector<HTMLElement>("#fl-main");
  if (!main) return;
  main.style.height = `${Math.max(300, window.innerHeight - main.getBoundingClientRect().top)}px`;
}

/** A tap inside a sheet never sends it back to the top (CLAUDE.md). The print card keeps its own (print-card.ts). */
function drawOverlay(): void {
  if (!host || !st) return;
  const o = host.root.querySelector<HTMLElement>("#fl-overlay");
  if (!o) return;
  const kind = st.sheet && st.sheet.kind !== "preview" ? st.sheet.kind : "";
  const kept = kind && kind === lastSheet ? SHEET_SCROLLERS.map((sel) => host!.root.querySelector<HTMLElement>(sel)?.scrollTop ?? 0) : [];
  drawOverlayInner(o);
  loadThumbs(o);
  lastSheet = st.sheet && st.sheet.kind !== "preview" ? st.sheet.kind : "";
  SHEET_SCROLLERS.forEach((sel, i) => {
    const el = host!.root.querySelector<HTMLElement>(sel);
    if (el && kept[i]) el.scrollTop = kept[i]!;
  });
}

function drawOverlayInner(o: HTMLElement): void {
  if (!host || !st) return;
  const s = st;
  const sh = s.sheet;
  if (sh?.kind === "preview") {
    if (sh.pc.room && !s.data.rooms.some((r) => r.name === sh.pc.room)) sh.pc.room = "";
    o.innerHTML = fieldCardHtml(s, sh);
    return;
  }
  if (sh?.kind === "rooms") {
    if (sh.sel && !s.data.rooms.some((r) => r.name === sh.sel)) sh.sel = s.cur;
    o.innerHTML = roomsSheet({ site: s.data, cur: sh.sel, kind: s.pick.kind, nums: s.pick.nums, part: s.pick.part, changes: s.roomsStart?.steps ?? 0, recode: s.recode });
    return;
  }
  if (sh?.kind === "sizes") {
    o.innerHTML = sizesCard();
    return;
  }
  if (sh?.kind === "types") {
    const quick = s.cur === OUTSIDE ? QUICK_OUTSIDE : QUICK_INSIDE;
    o.innerHTML = typesSheet(WORKBOOK_CLASSES.filter((c) => !quick.includes(c.code)).map((c) => ({ code: c.code, name: c.name })));
    return;
  }
  if (sh?.kind === "panelpick") {
    const r = room();
    const offer = r ? subpanelOffer(r, "PNL") : null;
    if (r && offer && offer.parent.id === sh.parentId) {
      const nums = r.gear.filter((x) => x.type === "PNL").map((x) => Number(/-(\d+)$/.exec(x.name)?.[1] ?? 0));
      let n = 1;
      while (nums.includes(n)) n++;
      o.innerHTML = panelPickSheet(`PNL-${String(n).padStart(2, "0")}`, offer.name, offer.parent.name);
      return;
    }
    s.sheet = null;
  }
  if (sh?.kind === "rename") {
    const f = gearById(sh.gearId);
    if (f) {
      o.innerHTML = renameSheet(f.g.name, f.g.type, sh.value, renameProblem(f.r, f.g.id, sh.value));
      return;
    }
    s.sheet = null;
  }
  if (sh?.kind === "photo") {
    const t = photoTarget(sh.room, sh.gearId);
    if (t?.photo) {
      o.innerHTML = photoSheet(t.title, t.photo.path, t.drive);
      return;
    }
    s.sheet = null;
  }
  if (sh?.kind === "rmroom") {
    const r = s.data.rooms.find((x) => x.name === sh.name);
    if (r) {
      o.innerHTML = confirmSheet(`Remove ${r.name}?`, `${r.name} and its ${r.gear.length} piece${r.gear.length === 1 ? "" : "s"} of gear come out. Undo brings them back.`, "rmroomgo");
      return;
    }
    s.sheet = null;
  }
  const m = s.menu;
  const f = m ? gearById(m.gearId) : null;
  if (!m || !f) {
    s.menu = null;
    o.innerHTML = "";
    return;
  }
  let title = f.g.name;
  let items = "";
  if (m.kind === "sq") {
    const letter = roomLetters(f.r).get(f.g.id)?.[m.sec] ?? "?";
    title = `${letter}${m.space} on ${f.g.name}`;
    items = squareMenuItems(f.g, letter, m.sec, m.space);
  } else if (m.kind === "bl") {
    title = `Circuit ${m.n} · Blank`;
    items = blankMenuItems();
  } else if (m.kind === "sh") {
    const letter = roomLetters(f.r).get(f.g.id)?.[m.sec] ?? "?";
    title = `Section ${m.sec + 1} · ${letter} on ${f.g.name}`;
    items = coverMenuItems(f.g, m.sec);
  } else if (m.kind === "cc") {
    title = `${breakerNames(f.g).get(m.top) ?? ""} · circuit${(f.g.circuits[String(m.top)] ?? 1) > 1 ? "s" : ""} ${circuitsOf(m.top, f.g.circuits[String(m.top)] ?? 1)}`;
    items = circuitMenuItems(f.g, m.top);
  } else {
    const labels = s.lineup.labels.filter((l) => l.gearId === f.g.id);
    const own = sizeOfLabel(labels[0]!, s.lineup, s.lineup.choices);
    // A panel with Breaker Labels Off and no main prints nothing at breaker size, so there is no size to pick.
    const kids = labels[1] ? sizeOfLabel(labels[1], s.lineup, s.lineup.choices) : isSec(f.g) ? DEFAULT_SIZES.bucket : isPanel(f.g) && f.g.breakerLabels ? DEFAULT_SIZES.breaker : null;
    items = labelMenuItems(f.g, own, kids);
  }
  const b = m.anchor;
  o.innerHTML = menuHtml(title, items, Math.max(8, Math.min(b.left, window.innerWidth - 250)), b.bottom + 6);
  // Keep the whole menu on the screen: above the control when there is no room below it.
  const el = o.querySelector<HTMLElement>(".fl-menu");
  if (el) {
    const hgt = el.getBoundingClientRect().height;
    const vh = window.visualViewport?.height ?? window.innerHeight;
    if (b.bottom + 6 + hgt > vh - 8) el.style.top = `${Math.max(8, b.top - 6 - hgt)}px`;
  }
}

// ---- Changes ----

const snap = (): string => JSON.stringify({ rooms: st!.data.rooms, cur: st!.cur, open: st!.open });

/** Every change keeps a copy of the site before it, so Undo takes it back whatever it was. */
function change(fn: (s: State) => void): void {
  if (!st) return;
  const before = snap();
  st.history.push(before);
  if (st.history.length > UNDO_STEPS) st.history.shift();
  st.menu = null;
  st.flash = null;
  st.notice = "";
  st.good = "";
  if (st.roomsStart) st.roomsStart.steps += 1;
  if (st.orderStart) st.orderStart.steps += 1;
  // Names hold once gear is on the sheet (Robert 2026-10-29 4:18 PM, option A): a remove or a drag never renumbers
  // it, so a saved Asset ID never moves to another piece of gear. PNL-01 out leaves a gap; PNL-02 stays PNL-02.
  holdSheetNames(st.data);
  const had = new Set(st.data.rooms.flatMap((r) => r.gear.map((g) => g.id)));
  fn(st);
  skipHeldIds(had);
  // The lock (ASSESSMENT_FLOW_B7_PART2_WORDS.md item 2): a change that would lose a saved Asset ID of locked gear
  // does not stand. It goes back, and the line says which gear and how to open it.
  const lost = lostHere(before);
  if (lost.length) {
    st.history.pop();
    const v = JSON.parse(before) as { rooms: unknown; cur: string | null; open: string | null };
    putBack(v.rooms);
    st.cur = v.cur;
    st.open = v.open;
    if (st.roomsStart) st.roomsStart.steps = Math.max(0, st.roomsStart.steps - 1);
    if (st.orderStart) st.orderStart.steps = Math.max(0, st.orderStart.steps - 1);
    st.notice = lockedWords(lost);
    drawParts();
    return;
  }
  // Unlocked gear lost saved rows: say they leave the sheet on the next push, and the labels on the door (item 5).
  st.notice = goneWords(before);
  writeSaved();
  drawParts();
}

/** "00-ER2-MCC-01-A3 and A4 come off the Assessment Sheet on the next push." Empty when nothing saved was lost. */
function goneWords(before: string): string {
  if (!st || !push || push.site !== st.site || push.company !== st.company || !push.docs) return "";
  const was = readSite({ rooms: (JSON.parse(before) as { rooms: unknown }).rooms });
  const gone = lockedLost(was, st.data, st.site, push.classes, push.docs, new Set()).map((l) => l.id);
  if (!gone.length) return "";
  return offWords(gone);
}

/**
 * After Undo: the saved gear still out of the walk, the same list the push button counts as Remove (pushChanges).
 * Empty when the step back put everything back (Robert 2026-10-29 4:40 PM: Undo clears the amber line).
 */
function stillOffWords(): string {
  if (!st || !push || push.site !== st.site || push.company !== st.company || !push.docs) return "";
  return offWords(pushChanges(st.data, st.site, push.classes, push.docs).remove);
}

/** The amber line for saved Asset IDs leaving the sheet, with the door note for any whose QR label printed. */
function offWords(gone: readonly string[]): string {
  if (!st || !push || !push.docs || !gone.length) return "";
  const docs = push.docs;
  const printed = gone.filter((id) => Boolean(docs[id]?.qrLabelPrintedAt) || Boolean(st!.data.printed?.[`qr|${id}`]));
  return sheetOffWords(gone, printed);
}

/**
 * A room with gear on the sheet keeps every name in it: marked named, the way a Rename holds one
 * (field-labels-site.ts nameInOrder). Removing PNL-01 leaves a gap; the unsaved PNL-02 stays PNL-02 too.
 */
function holdSheetNames(site: FieldSite): void {
  const on = onSheetHere();
  if (!on.size) return;
  for (const r of site.rooms) {
    if (!r.gear.some((g) => on.has(g.id))) continue;
    for (const g of r.gear) if (g.type !== "MCC") g.named = true;
  }
}

/**
 * Item 12: new gear in a room with gear on the sheet never takes an Asset ID the sheet has held, live or removed.
 * It takes the next number after every one the sheet has held for that type in that room, so a removed row can
 * never come back on a different panel. MCCs keep their own naming (MCC-1A, MCC-1B).
 */
function skipHeldIds(had: ReadonlySet<string>): void {
  if (!st || !push || push.site !== st.site || push.company !== st.company || !push.docs) return;
  const docs = push.docs;
  const on = onSheetHere();
  for (const r of st.data.rooms) {
    if (!r.gear.some((g) => on.has(g.id))) continue;
    for (const g of r.gear) {
      if (had.has(g.id)) continue;
      const name = heldName(r, g, st.site, push.classes, docs);
      if (!name) continue;
      g.name = name;
      g.named = true;
    }
  }
}

/** Gear id to its saved, live Asset IDs, from the sheet's records read when the page opened. Empty until read. */
function onSheetHere(): Map<string, string[]> {
  if (!st || !push || push.site !== st.site || push.company !== st.company || !push.docs) return new Map();
  return onSheetGear(st.data, st.site, push.classes, push.docs);
}

/** The saved Asset IDs of locked gear that the walk as it is now no longer makes, against the walk before. */
function lostHere(before: string): { id: string; gearId: string }[] {
  if (!st || !push || push.site !== st.site || push.company !== st.company || !push.docs) return [];
  const was = readSite({ rooms: (JSON.parse(before) as { rooms: unknown }).rooms });
  return lockedLost(was, st.data, st.site, push.classes, push.docs, st.unlocked);
}

/** "MCC-01 is on the Assessment Sheet. Tap Unlock on its card to change it." */
function lockedWords(lost: readonly { gearId: string }[]): string {
  const names = [...new Set(lost.map((l) => gearById(l.gearId)?.g.name).filter((n): n is string => Boolean(n)))];
  const who = names.length ? (names.length === 1 ? names[0]! : `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`) : "This gear";
  return `${who} ${names.length > 1 ? "are" : "is"} on the Assessment Sheet. Tap Unlock on ${names.length > 1 ? "each card" : "its card"} to change it.`;
}

/** On Sheet and not unlocked: a rename or remove asks for Unlock first. */
function lockedGear(gearId: string): boolean {
  return Boolean(st && !st.unlocked.has(gearId) && onSheetHere().has(gearId));
}

/**
 * Undo and both Cancels put the rooms back as they were. Printing and photos are not edits: the printed record,
 * the Drive order and every photo stay as they are now (carryPhotos, ROOM_GEAR_PHOTO_B5_WORDS.md).
 */
function putBack(rooms: unknown): void {
  if (!st) return;
  const was = st.data;
  st.data = readSite({ rooms });
  if (was.printed) st.data.printed = was.printed;
  if (was.sent) st.data.sent = was.sent;
  carryPhotos(was, st.data);
}

// ---- Push By Room (PUSH_BY_ROOM_B7_WORDS.md, MOCKUP_PUSH_BY_ROOM.html approved 2026-10-29 5:05 PM) ----

/** The gate and what the sheet already holds, per site. Read when the page opens. */
let push: { site: string; company: string; onSheet: Set<string> | null; classes: readonly ClassOption[]; docs: Record<string, Record<string, unknown>> | null } | null = null;

/** The button's numbers, from the walk as it is now. Pure Bring In, so it costs nothing to work out on every draw. */
function pushView(): PushView | undefined {
  if (!st) return undefined;
  const r = room();
  if (!r) return { room: null, done: false, first: true, fresh: null };
  const mark = st.data.pushed?.[r.name];
  if (!push || push.site !== st.site || push.company !== st.company || !push.onSheet) return { room: r.name, done: Boolean(r.done), first: !mark, fresh: null };
  // Item 7: rows already waiting in the run on this iPad are not new (the 222 New, field test 2026-10-29 3:37 PM).
  const waiting = waitingIds(readStore, st.company, st.site, push.classes);
  const b = bringIn(roomOnly(st.data, r.name), st.site, push.classes, new Set([...push.onSheet, ...waiting]));
  const ch = roomChanges(st.data, r.name, st.site, push.classes, push.docs);
  return { room: r.name, done: Boolean(r.done), first: !mark && b.already === 0, fresh: b.rows.length, remove: ch.remove.length + ch.goneRemove.length, restore: ch.restore.length + ch.goneRestore.length };
}

/** Swaps the bar button in place: no redraw, so nothing scrolls. */
function drawPush(): void {
  const el = host?.root.querySelector<HTMLElement>("#fl-push");
  if (el) el.outerHTML = pushButton(pushView());
}

/** Reads the 00 mark and the sheet's rows. Either can fail (no signal, no pack here): the button says less. */
async function readPush(company: string, site: string): Promise<void> {
  const mine = { site, company, onSheet: null as Set<string> | null, classes: WORKBOOK_CLASSES as readonly ClassOption[], docs: null as Record<string, Record<string, unknown>> | null };
  push = mine;
  // Two reads, each drawn as it lands. The mark alone turns the button on (field test 2026-10-29 8:36 AM: after a
  // reload the rows read from this iPad's store stalled and held the button grey).
  const rows = (async () => {
    try {
      const pack = host?.pack && host.pack.siteNumber === site ? host.pack : await withTimeLimit(readSitePack(company, { siteNumber: site, name: host?.siteName ?? site }, false), 15_000);
      mine.onSheet = new Set([...Object.keys(pack.assetDocs ?? {}), ...pack.assets.map((a) => a.assetId)]);
      mine.classes = classesOf(pack);
      // The QR block needs the sheet's rows as stored: one QR PDF holds the sheet's labels and the walk's (qr-roll.ts).
      mine.docs = pack.assetDocs ?? Object.fromEntries(pack.assets.map((a) => [a.assetId, { location: a.location, nominalVoltage: a.nominalVoltage }]));
    } catch {
      mine.onSheet = null;
    }
    if (push === mine) {
      // The sheet's rows landed: On Sheet pills and locks come in. Not while typing: the box would lose the keyboard.
      if (host && !typingHere(host)) drawParts();
      else drawPush();
      // The print card open while the rows landed: the QR block comes in.
      if (st?.sheet?.kind === "preview") drawOverlay();
    }
  })();
  // 00 Field Labels Mark Complete is a tracker only now (PUSH_BY_ROOM_B7_WORDS.md item 4): Room Done gates the push.
  if (push === mine) drawPush();
  await rows;
}

// ---- Room and gear photos (ROOM_GEAR_PHOTO_B5_WORDS.md, MOCKUP_ROOM_GEAR_PHOTO.html approved 2026-10-29) ----

interface PhotoTarget {
  /** "ER2 · Room Photo", "PNL-01 · Photo". */
  title: string;
  /** "ER2", "PNL-01": what the camera says it is shooting. */
  what: string;
  photo: WalkPhoto | undefined;
  /** The Drive name now: 00_ER2.jpg, 00_ER2_PNL-01.jpg. */
  drive: string;
  set: (p: WalkPhoto) => void;
}

function photoTarget(roomName: string, gearId: string): PhotoTarget | null {
  if (!st) return null;
  const site = st.site;
  if (gearId) {
    const f = gearById(gearId);
    if (!f) return null;
    return { title: `${f.g.name} · Photo`, what: f.g.name, photo: f.g.photo, drive: photoDriveName(site, f.r.name, f.g.name), set: (p) => (f.g.photo = p) };
  }
  const r = st.data.rooms.find((x) => x.name === roomName);
  if (!r) return null;
  return { title: `${r.name} · Room Photo`, what: r.name, photo: r.photo, drive: photoDriveName(site, r.name), set: (p) => (r.photo = p) };
}

const photoWho = () => ({ company: st!.company, site: st!.site, badgeId: host!.badge.badgeId, byName: host!.badge.holderName });

/** The camera for one room or gear photo. The shot replaces the one there; it is not an Undo step. */
function shootPhoto(roomName: string, gearId: string): void {
  const t0 = photoTarget(roomName, gearId);
  if (!t0) return;
  const cam = openCamera({
    title: t0.title,
    label: gearId ? `${t0.what} · Door Open` : t0.what,
    run: false,
    onShot: (shot) => {
      cam.close();
      void (async () => {
        // Found again after the shot: the walk may have moved while the camera was up.
        const t = photoTarget(roomName, gearId);
        if (!t || !st || !host) return;
        try {
          t.set(await takePhoto(photoWho(), t.drive, shot.blob, t.photo));
        } catch {
          st.notice = "The photo did not save on this iPad. Take it again.";
        }
        if (st.sheet?.kind === "photo") st.sheet = null;
        writeSaved();
        drawParts();
      })();
    },
    onSkip: () => undefined,
    onDone: () => undefined,
  });
}

let renaming = false;

/**
 * After a save: a photo whose room was re-coded or whose gear was renamed goes again under the name it has now.
 * Not an edit, so no Undo step. One pass at a time; a pass that changed something saves, which looks once more.
 */
async function sendRenamedPhotos(): Promise<void> {
  if (renaming || !st || !host) return;
  const s = st;
  const list = photosBehind(s.data, s.site);
  if (!list.length) return;
  renaming = true;
  let moved = false;
  try {
    for (const b of list) {
      const rec = await resendPhoto(photoWho(), b.photo, b.want);
      if (!rec || st !== s) continue;
      const t = photoTarget(b.room, b.gearId);
      // Only when the walk still holds this same photo there.
      if (t?.photo?.id === b.photo.id) {
        t.set(rec);
        moved = true;
      }
    }
  } catch {
    // Tried again on the next save.
  } finally {
    renaming = false;
  }
  if (moved && st === s) {
    writeSaved();
    drawParts();
  }
}

function undo(): void {
  if (!st) return;
  const prev = st.history.pop();
  if (!prev) return;
  const v = JSON.parse(prev) as { rooms: unknown; cur: string | null; open: string | null };
  // Undo keeps the lock too: a step back that would lose a saved Asset ID of locked gear does not happen.
  const now = snap();
  const lost = (() => {
    if (!push || push.site !== st.site || push.company !== st.company || !push.docs) return [];
    return lockedLost(readSite({ rooms: (JSON.parse(now) as { rooms: unknown }).rooms }), readSite({ rooms: v.rooms }), st.site, push.classes, push.docs, st.unlocked);
  })();
  if (lost.length) {
    st.history.push(prev);
    st.notice = lockedWords(lost);
    drawParts();
    return;
  }
  // Undo takes back edits, never a print or a photo: the printed record and the photos stay as they are.
  putBack(v.rooms);
  st.cur = v.cur;
  st.open = v.open;
  st.menu = null;
  st.sheet = null;
  st.flash = null;
  st.typing = "";
  st.roomsStart = null;
  if (st.orderStart) st.orderStart.steps = Math.max(0, st.orderStart.steps - 1);
  st.notice = stillOffWords();
  writeSaved();
  drawParts();
}

/** Opens Rooms and keeps the site as it is now, for Cancel. */
function openRooms(): void {
  if (!st) return;
  st.roomsStart = { rooms: JSON.stringify(st.data.rooms), cur: st.cur, open: st.open, history: [...st.history], steps: 0 };
  st.recode = null;
  st.sheet = { kind: "rooms", sel: st.cur };
  drawOverlay();
}

/**
 * Cancel in Rooms: every room added, changed or removed since it opened goes back, and you are back in the room
 * you were in. What printed stays as it is: printing is not an edit. Undo forgets the cancelled steps.
 */
function cancelRooms(): void {
  if (!st) return;
  const start = st.roomsStart;
  st.roomsStart = null;
  st.sheet = null;
  if (start && start.steps > 0) {
    putBack(JSON.parse(start.rooms));
    st.cur = start.cur;
    st.open = start.open;
    st.history = start.history;
    st.menu = null;
    st.flash = null;
    writeSaved();
  }
  drawParts();
}

/** Order: keep the room as it is now, for Cancel. */
function startOrder(): void {
  if (!st) return;
  st.ordering = true;
  st.orderStart = { rooms: JSON.stringify(st.data.rooms), open: st.open, history: [...st.history], steps: 0 };
  st.open = null;
  st.menu = null;
  drawParts();
}

/** Cancel in Order: the room goes back to its order and names when Order was tapped. Printing stays as it is. */
function cancelOrder(): void {
  if (!st) return;
  const start = st.orderStart;
  st.ordering = false;
  st.orderStart = null;
  if (start && start.steps > 0) {
    putBack(JSON.parse(start.rooms));
    st.open = start.open;
    st.history = start.history;
    st.menu = null;
    st.flash = null;
    writeSaved();
  }
  drawParts();
}

/** The code a tapped room starts from in Change ER6 To: its own. */
function recodeOf(name: string | null): State["recode"] {
  const m = /^(ER|EA)(\d+)([A-Z]?)$/.exec(name ?? "");
  return m ? { kind: m[1] as "ER" | "EA", num: Number(m[2]), part: m[3] ?? "" } : null;
}

/** Tap a label in the preview: the preview closes, the page goes to its room, opens its gear and outlines it red. */
function goTo(key: string): void {
  if (!st || !host) return;
  const l = st.lineup.labels.find((x) => x.key === key) as SiteLabel | undefined;
  if (!l) return;
  st.sheet = null;
  st.cur = l.room;
  st.open = l.gearId;
  st.ordering = false;
  st.flash = { gearId: l.gearId, sec: l.sec, space: l.space, circuit: l.circuit, own: l.kind === "big" || l.kind === "housing" };
  writeSaved();
  drawParts();
  const t = host.root.querySelector<HTMLElement>("#fl-right .flash");
  t?.scrollIntoView({ block: "center" });
  setTimeout(() => {
    if (st) st.flash = null;
    t?.classList.remove("flash");
  }, 1800);
}

function tapSquare(el: HTMLElement): void {
  if (!st) return;
  const f = gearById(el.dataset.g ?? "");
  if (!f) return;
  const sec = Number(el.dataset.s);
  const space = Number(el.dataset.k);
  st.open = f.g.id;
  const count = f.g.sections[sec];
  if (count === undefined) {
    if (!nextLetter(f.r, f.g.id)) return;
    return change(() => fillTo(f.g, sec, space));
  }
  if (space > count) return change(() => fillTo(f.g, sec, space));
  st.menu = { kind: "sq", gearId: f.g.id, sec, space, anchor: el.getBoundingClientRect() };
  drawOverlay();
}

function tapCircuit(el: HTMLElement): void {
  if (!st) return;
  const f = gearById(el.dataset.g ?? "");
  if (!f) return;
  const n = Number(el.dataset.n);
  st.open = f.g.id;
  if (isBlank(f.g, n)) {
    st.menu = { kind: "bl", gearId: f.g.id, n, anchor: el.getBoundingClientRect() };
    drawOverlay();
    return;
  }
  if (!f.g.circuits[String(n)]) return change(() => addBreaker(f.g, n));
  st.menu = { kind: "cc", gearId: f.g.id, top: n, anchor: el.getBoundingClientRect() };
  drawOverlay();
}

/** A size picked on the gear's label menu. A pick equal to the default is stored as no pick. */
function pickSize(which: "size" | "kidSize", k: string): void {
  const m = st?.menu;
  const f = m ? gearById(m.gearId) : null;
  const size = sizeFromKey(k);
  if (!f || !size) return;
  const def: LabelSize = which === "size" ? (f.g.type === "MCC" || f.g.type === "SWBD" || f.g.type === "SWGR" ? DEFAULT_SIZES.big : DEFAULT_SIZES.housing) : isPanel(f.g) ? DEFAULT_SIZES.breaker : DEFAULT_SIZES.bucket;
  change(() => (f.g[which] = sizeKey(def) === k ? "" : k));
}

/**
 * Print At, in the preview (Robert, 2026-10-23, 3:17 PM): a block of labels prints at another size,
 * for a customer who wants smaller or bigger, or a reprint. For this print only (3:45 PM): nothing on the gear
 * changes, and closing the preview puts every block back on its own size.
 */
function printAt(from: string, to: string): void {
  if (st?.sheet?.kind !== "preview" || !sizeFromKey(to) || !sizeFromKey(from)) return;
  const sh = st.sheet;
  if (from === to) delete sh.over[from];
  else sh.over[from] = to;
  // A print file made for this block before is out of date.
  sh.pc.small.delete(from);
  drawOverlay();
}

/** The blocks to print: one per paper size the labels would take, with the size picked for this print. */
export function blocksOf(l: SiteLineup, over: Readonly<Record<string, string>>): PrintBlock[] {
  return printFiles(l, l.choices).map((f) => ({ ...f, print: sizeFromKey(over[sizeKey(f.size)] ?? "") ?? f.size }));
}

// ---- The print card (rev 5: one PDF per size in Drive, 10_Field_Labels_1x2.pdf) ----

export type PreviewSheet = Extract<Sheet, { kind: "preview" }>;

/** A field label drawn to scale, as the preview always drew it: yellow tape, its own size, outlined when it won't hold full height. */
function fieldArt(l: SiteLabel, ps: LabelSize, tooTall: boolean): string {
  const fit = fitOf(l.text, ps);
  const style = `width:${ps.length * PREVIEW_PX}px;height:${ps.height * PREVIEW_PX}px;font-size:${((fit.points * PREVIEW_PX) / 72).toFixed(1)}px`;
  return `<span class="fl-pvlab pc-flart${l.spare ? " spare" : ""}${tooTall ? " over" : ""}" style="${style}">${esc(l.text)}</span>`;
}

/**
 * One roll per paper size, every label at the site, room by room in hanging order. The master keeps each
 * label's own size. Print At changes only the small print file. Exported for the tests.
 */
export function fieldRolls(s: Pick<State, "site" | "data" | "lineup">, sh: PreviewSheet): PcRoll[] {
  const reds = new Set(s.lineup.problems.filter((p) => p.tone === "red" && p.key).map((p) => p.key!));
  return blocksOf(s.lineup, sh.over).map((f) => {
    const k = sizeKey(f.size);
    const ps = f.print;
    const off = sizeKey(ps) !== k;
    const chips = TORO_MAX_SIZES.map((sz) => {
      const sk = sizeKey(sz);
      return `<button type="button" class="fl-pvsz${sk === sizeKey(ps) ? " cur" : ""}${sk === k ? " def" : ""}" data-fl="blocksize" data-from="${k}" data-size="${sk}">${sizeName(sz)}${sk === k ? "<small>Default</small>" : ""}</button>`;
    }).join("");
    const warn = off ? `<div class="fl-pvwarn">Make PDF prints these at ${sizeName(ps)} this time. Default is ${sizeName(f.size)}. The Drive PDF keeps ${sizeName(f.size)}.</div>` : "";
    return {
      key: k,
      title: `${sizeName(f.size)} in`,
      sub: `set the driver to ${driverName(driverPage(ps.height, ps.length))}`,
      driveName: masterName("fl", s.site, sizePart(f.size.height, f.size.length)),
      // A label size that isn't a driver size moved to a bigger page (2 x 9 onto GP 2 x 12): its old Drive PDF is behind.
      pageChange: driverPage(f.size.height, f.size.length).length !== f.size.length
        ? { sinceMs: PAGE_SIZES_SINCE_MS, note: `The page size changed to ${driverName(driverPage(f.size.height, f.size.length))}.` }
        : undefined,
      folder: "00 Field Labels",
      withRooms: true,
      extra: `<div class="fl-pvat"><span>Print At</span>${chips}</div>${warn}`,
      offDefault: off,
      moved: orderMoved(s.data.sent?.[k], f.labels as SiteLabel[]),
      roomLine: (room, n) => `${n} label${n === 1 ? "" : "s"} · ${sizeName(f.size)} in`,
      items: (f.labels as SiteLabel[]).map((l) => {
        const p = printStateOf(l, s.data.printed);
        const tooTall = !holdsOn(l, f.size);
        return {
          key: l.key,
          room: l.room,
          state: p.state,
          why: tooTall ? "Won't hold full height at this size" : p.state === "changed" ? `Was ${p.was}` : p.state === "new" ? "Not printed yet" : "",
          reprintWhy: p.state === "current" ? `Last printed ${shortDate(p.at)}` : undefined,
          art: fieldArt(l, f.size, tooTall),
          fix: reds.has(l.key) || tooTall,
          hay: `${l.room} ${l.housing} ${l.text}`,
        };
      }),
    } satisfies PcRoll;
  });
}

/**
 * QR labels beside the field labels (MOCKUP_ASSESSMENT_FLOW.html screen 5): for any gear in the walk, at Robert's
 * choice (6:48, 6:51 AM). The same roll the sheet prints (qr-roll.ts). Left off until the sheet's rows are read here,
 * so an Update PDF from this card can never drop the sheet's labels from 00_QR_Labels.pdf.
 */
export function qrRollHere(s: Pick<State, "site" | "data">, docs: Record<string, Record<string, unknown>> | null, classes: readonly ClassOption[]): PcRoll | null {
  if (!docs) return null;
  const roll = qrRollOf(qrEntries({ walk: s.data, siteNumber: s.site, classes, docs }), s.site, "QR Labels · 2.25 × 1.0 in");
  return roll.items.length ? roll : null;
}

/** Every roll on the field card: the field label sizes, then the QR block. */
function cardRolls(s: Pick<State, "site" | "data" | "lineup">, sh: PreviewSheet): PcRoll[] {
  const here = push && push.site === s.site ? push : null;
  const qr = qrRollHere(s, here?.docs ?? null, here?.classes ?? WORKBOOK_CLASSES);
  return [...fieldRolls(s, sh), ...(qr ? [qr] : [])];
}

export function fieldCardHtml(s: Pick<State, "site" | "data" | "lineup">, sh: PreviewSheet): string {
  const rolls = cardRolls(s, sh);
  seedPicks(sh.pc, rolls);
  const reds = s.lineup.problems.filter((p) => p.tone === "red" && (!sh.pc.room || !p.key || p.key.startsWith(`${sh.pc.room}|`)));
  const tall = rolls.flatMap((r) => r.items).filter((i) => i.fix && i.why.startsWith("Won't")).length;
  const custom = Object.keys(sh.over).length > 0 || s.data.rooms.some((r) => (!sh.pc.room || r.name === sh.pc.room) && r.gear.some((g) => g.size || g.kidSize));
  const shockHere = s.lineup.shock.filter((x) => !sh.pc.room || x.room === sh.pc.room);
  const shockRooms = [...new Set(shockHere.map((x) => x.room))].map((room) => `${room} · ${shockHere.filter((x) => x.room === room).length}`).join(", ");
  const top = [
    shockHere.length ? `<div class="note grey">Shock Labels · ${shockHere.length}: ${esc(shockRooms)}. Shock Only gear prints no field label. Each needs a shock label.</div>` : "",
    reds.length ? `<div class="note red">${esc(reds[0]!.text)}</div>` : "",
    tall ? `<div class="note">${tall} label${tall === 1 ? "" : "s"} won't hold full height on ${tall === 1 ? "its size" : "their sizes"}. Tap Fix to size up its gear.</div>` : "",
    custom ? `<div class="fl-pvreset"><span>Some labels are set to print at a size other than the default.</span><button class="ac-tbtn" type="button" data-fl="sizesreset">Back To Default Sizes</button></div>` : "",
  ].join("");
  const lead = (name: string) => {
    const r = s.data.rooms.find((x) => x.name === name);
    return r ? light(roomLight(r)) : "";
  };
  return printCard({ title: "Print Field Labels", roll: "fieldRoll", rolls, state: sh.pc, top, roomLead: lead, slim: true, roomOrder: s.data.rooms.map((r) => r.name), bottom: startOverHtml(s.site, sh) });
}

/** Start Printing Over, at the bottom of the card. Two taps: the button, then Clear (field test 2026-10-28 4:28 PM). */
export function startOverHtml(site: string, sh: PreviewSheet): string {
  if (!sh.startOver) return `<div class="fl-over"><button class="ac-tbtn" type="button" data-fl="startover">Start Printing Over</button></div>`;
  return `<div class="fl-over ask"><span>Every label at ${esc(site)} reads NEW again. Nothing on Drive is deleted.</span><span class="pc-sp"><button class="ac-tbtn" type="button" data-fl="startovercancel">Cancel</button><button class="ac-tbtn dark" type="button" data-fl="startoverclear">Clear</button></span></div>`;
}

/** A tap on the card that isn't a Print At chip (those keep their data-fl). */
async function fieldCardClick(el: HTMLElement): Promise<void> {
  const s = st!;
  const sh = s.sheet;
  if (sh?.kind !== "preview") return;
  const rolls = cardRolls(s, sh);
  const a = cardClick(el, sh.pc, rolls);
  if (!a) return;
  switch (a.act) {
    case "close":
      if (sh.pc.busy) return;
      lastPrintRoom = sh.pc.room;
      stopMasterWatches();
      s.sheet = null;
      break;
    case "setup":
      stopMasterWatches();
      s.sheet = null;
      location.hash = "#/settings/printer";
      return;
    case "fix":
      goTo(a.key);
      return;
    case "update": {
      const r = rolls.find((x) => x.key === a.block);
      if (r) await updateMasters(sh, [r]);
      return;
    }
    case "updateall":
      await updateMasters(sh, rolls.filter((r) => rollStatus(r, sh.pc).behind));
      return;
    case "make": {
      const r = rolls.find((x) => x.key === a.block);
      if (r) await makeSmall(sh, r);
      return;
    }
    case "share": {
      const f = sh.pc.small.get(a.block)?.file;
      if (f) await shareFile(f);
      return;
    }
    case "download": {
      const f = sh.pc.small.get(a.block)?.file;
      if (f) downloadCardFile(f);
      return;
    }
    case "mdownload": {
      const f = sh.pc.masterFiles.get(a.block);
      if (f) downloadCardFile(f);
      return;
    }
    case "retry": {
      const r = rolls.find((x) => x.key === a.block);
      if (r) void retryMaster(sh.pc, s.site, r.driveName, () => onThisPage() && drawOverlay());
      return;
    }
    default:
      break;
  }
  drawOverlay();
}

/**
 * Update PDF: every label at its own size, room by room with a room page first, at the roll's start offset.
 * Goes to 00 Field Labels as 10_Field_Labels_1x2.pdf (one per size), replacing the one there. The new and
 * changed ones now count as in it. Not an edit, so Undo never takes it back.
 */
async function updateMasters(sheet: PreviewSheet, rolls: readonly PcRoll[]): Promise<void> {
  if (!st || !host || sheet.pc.busy || !rolls.length) return;
  sheet.pc.busy = rolls.length > 1 ? "update:all" : `update:${rolls[0]!.key}`;
  sheet.pc.error = "";
  drawOverlay();
  try {
    for (const r of rolls) {
      if (r.key === "qr") {
        await updateQr(sheet, r);
        continue;
      }
      const size = sizeFromKey(r.key);
      if (!size) continue;
      const byKey = new Map(st.lineup.labels.map((l) => [l.key, l as SiteLabel]));
      const parts = await Promise.all(
        sectionsOf(r).map(async (sec) => ({ room: sec.room, line: r.roomLine!(sec.room, sec.items.length), bytes: await fieldLabelsPdf(sec.items.map((i) => byKey.get(i.key)!.text), size) })),
      );
      const bytes = await assembleMaster(`SafeTag Field Labels ${sizeName(size)} in, site ${st.site}`, parts);
      sheet.pc.masterFiles.set(r.key, new File([bytes as Uint8Array<ArrayBuffer>], r.driveName, { type: "application/pdf" }));
      await sendMaster(sheet.pc, { company: host.badge.company, site: st.site, kind: "fl", driveName: r.driveName, bytes, badgeId: host.badge.badgeId, byName: host.badge.holderName }, () => onThisPage() && drawOverlay());
      const going = r.items.filter((i) => i.state !== "current").map((i) => byKey.get(i.key)!);
      if (going.length) st.data = markPrinted(st.data, going, new Date().toISOString());
      st.data = markSent(st.data, r.key, r.items.filter((i) => !i.skip).map((i) => byKey.get(i.key)!));
      writeSaved();
    }
  } catch (e) {
    sheet.pc.error = `Could not update the PDF: ${e instanceof Error ? e.message : String(e)}`;
  }
  sheet.pc.busy = "";
  if (onThisPage()) drawOverlay();
}

/**
 * The QR block's Update PDF: the whole site's QR labels, the same file the sheet makes, to 02 QR Labels. The ones
 * that went in are marked in the walk; the sheet stamps its saved rows from that mark when it opens.
 */
async function updateQr(sheet: PreviewSheet, r: PcRoll): Promise<void> {
  if (!st || !host) return;
  const parts = await Promise.all(sectionsOf(r).map(async (sec) => ({ room: sec.room, line: `${sec.items.length} label${sec.items.length === 1 ? "" : "s"}`, bytes: await qrLabelsPdf(sec.items.map((i) => i.key)) })));
  const bytes = await assembleMaster(`SafeTag QR Labels, site ${st.site}`, parts);
  sheet.pc.masterFiles.set(r.key, new File([bytes as Uint8Array<ArrayBuffer>], r.driveName, { type: "application/pdf" }));
  await sendMaster(sheet.pc, { company: host.badge.company, site: st.site, kind: "qr", driveName: r.driveName, bytes, badgeId: host.badge.badgeId, byName: host.badge.holderName }, () => onThisPage() && drawOverlay());
  st.data = markQrPrinted(st.data, r.items.filter((i) => i.state === "new").map((i) => i.key), new Date().toISOString());
  writeSaved();
}

/** Marks QR labels printed in the walk. Not an edit: Undo never takes it back, the same as field labels. */
export function markQrPrinted(site: FieldSite, ids: readonly string[], at: string): FieldSite {
  if (!ids.length) return site;
  const printed = { ...(site.printed ?? {}) };
  for (const id of ids) printed[qrPrintKey(id)] = { text: id, at };
  return { ...site, printed };
}

/**
 * The sheet printed QR labels: mark them in this site's walk too, so the field card reads them as printed. The walk
 * on this iPad is read, marked and saved; the Outbox sends it like any other walk save.
 */
export function stampQrInWalk(company: string, site: string, ids: readonly string[], at: string): void {
  if (!ids.length) return;
  if (st && st.company === company && st.site === site) {
    st.data = markQrPrinted(st.data, ids, at);
    writeSaved();
    return;
  }
  try {
    const raw = readStore(storageKey(company, site));
    if (!raw) return;
    const saved = JSON.parse(raw) as { rooms?: unknown; printed?: unknown; sent?: unknown; cur?: string | null };
    const data = markQrPrinted(readSite(saved), ids, at);
    const savedAt = new Date().toISOString();
    window.localStorage.setItem(storageKey(company, site), JSON.stringify({ ...walkMarks(data), cur: saved.cur ?? null, savedAt }));
  } catch {
    // Not marked here. The sheet's own stamp still says it printed.
  }
}

/** The green line waiting for Field Labels after a push, by company and site. Taken once when the page opens. */
let pushedLine: { company: string; site: string; text: string } | null = null;

function takePushedLine(company: string, site: string): string {
  if (!pushedLine || pushedLine.company !== company || pushedLine.site !== site) return "";
  const t = pushedLine.text;
  pushedLine = null;
  return t;
}

/** The walk as this iPad holds it: the open page's copy, or the stored one. Null when there is none. */
function walkFor(company: string, site: string): { data: FieldSite; cur: string | null } | null {
  if (st && st.company === company && st.site === site) return { data: st.data, cur: st.cur };
  try {
    const raw = readStore(storageKey(company, site));
    if (!raw) return null;
    const saved = JSON.parse(raw) as { cur?: string | null };
    return { data: readSite(saved), cur: saved.cur ?? null };
  } catch {
    return null;
  }
}

/** Keeps a changed walk: the open page saves and sends it; otherwise this iPad's copy, sent when Field Labels opens. */
function keepWalk(company: string, site: string, data: FieldSite, cur: string | null): void {
  if (st && st.company === company && st.site === site) {
    st.data = data;
    writeSaved();
    return;
  }
  try {
    window.localStorage.setItem(storageKey(company, site), JSON.stringify({ ...walkMarks(data), cur, savedAt: new Date().toISOString() }));
  } catch {
    // Not kept here. The sheet still holds what the push sent.
  }
}

/**
 * A room pushed from the Assessment Sheet (PUSH_BY_ROOM_B7_WORDS.md item 8): the walk keeps the room's push, every
 * Asset ID it makes, so its gear reads green On Sheet on every iPad. Returns the line: "ER1 pushed to the
 * Assessment Sheet." the first time, "ER1 On Sheet updated." after. Field Labels shows it too when it opens.
 */
export function markRoomPushed(company: string, site: string, roomName: string, classes: readonly ClassOption[], at: string): string {
  const w = walkFor(company, site);
  if (!w || !w.data.rooms.some((r) => r.name === roomName)) return "";
  const first = !w.data.pushed?.[roomName];
  const ids = walkRowsOf(roomOnly(w.data, roomName), site, classes).rows.map((x) => x.id);
  keepWalk(company, site, markPushed(w.data, roomName, ids, at), w.cur);
  const text = first ? `${roomName} pushed to the Assessment Sheet.` : `${roomName} On Sheet updated.`;
  pushedLine = { company, site, text };
  return text;
}

/** Rows cleared from the run before they saved (Clear The New Row): they lose their green On Sheet. */
export function unpushInWalk(company: string, site: string, ids: readonly string[]): void {
  if (!ids.length) return;
  const w = walkFor(company, site);
  if (!w || !w.data.pushed) return;
  keepWalk(company, site, unmarkPushed(w.data, ids), w.cur);
}

/** Make PDF: just the picked labels, at Print At's size and this iPad's offset. For the printer. Never to Drive. */
async function makeSmall(sheet: PreviewSheet, r: PcRoll): Promise<void> {
  if (!st || sheet.pc.busy) return;
  const picked = pickedOf(r, sheet.pc);
  if (!picked.length) return;
  if (r.key === "qr") return makeQrSmall(sheet, r, picked.map((i) => i.key));
  const print = sizeFromKey(sheet.over[r.key] ?? "") ?? sizeFromKey(r.key);
  if (!print) return;
  sheet.pc.busy = `make:${r.key}`;
  sheet.pc.error = "";
  drawOverlay();
  try {
    const byKey = new Map(st.lineup.labels.map((l) => [l.key, l as SiteLabel]));
    const bytes = await fieldLabelsPdf(picked.map((i) => byKey.get(i.key)!.text), print, readOffset("fieldRoll"));
    const name = `${st.site}_Field_Labels_Print_${sizePart(print.height, print.length)}${sheet.pc.room ? `_${sheet.pc.room}` : ""}.pdf`;
    sheet.pc.small.set(r.key, { file: new File([bytes as Uint8Array<ArrayBuffer>], name, { type: "application/pdf" }), count: picked.length });
  } catch (e) {
    sheet.pc.error = `Could not make the PDF: ${e instanceof Error ? e.message : String(e)}`;
  }
  sheet.pc.busy = "";
  if (onThisPage()) drawOverlay();
}

/** The QR block's Make PDF: the picked QR labels at this iPad's QR roll offset. For the printer. Never to Drive. */
async function makeQrSmall(sheet: PreviewSheet, r: PcRoll, ids: readonly string[]): Promise<void> {
  if (!st) return;
  sheet.pc.busy = `make:${r.key}`;
  sheet.pc.error = "";
  drawOverlay();
  try {
    const bytes = await qrLabelsPdf(ids, readOffset("qrRoll"));
    const name = `${st.site}_QR_Labels_Print_${ids.length === 1 ? ids[0]!.replace(/^\d{2}-/, "") : `${ids.length}_labels`}.pdf`;
    sheet.pc.small.set(r.key, { file: new File([bytes as Uint8Array<ArrayBuffer>], name, { type: "application/pdf" }), count: ids.length });
  } catch (e) {
    sheet.pc.error = `Could not make the PDF: ${e instanceof Error ? e.message : String(e)}`;
  }
  sheet.pc.busy = "";
  if (onThisPage()) drawOverlay();
}

/** The menu's gear, read fresh after the click. */
function menuGear(): { r: SiteRoom; g: Gear; m: Menu } | null {
  const m = st?.menu;
  const f = m ? gearById(m.gearId) : null;
  return m && f ? { ...f, m } : null;
}

/** Opens the card. gearId: Reprint Labels from a gear's menu, that gear and everything in it picked. */
function openCard(gearId = ""): void {
  if (!st || !host) return;
  const pc = newPcState(lastPrintRoom);
  if (gearId) {
    const f = gearById(gearId);
    pc.room = f?.r.name ?? "";
    for (const l of st.lineup.labels as SiteLabel[]) if (l.gearId === gearId) pc.picks.add(l.key);
    // Only this gear: nothing else comes picked.
    for (const r of fieldRolls(st, { kind: "preview", pc, over: {} })) pc.seeded.add(r.key);
  }
  if (pc.room && !st.data.rooms.some((r) => r.name === pc.room)) pc.room = "";
  st.sheet = { kind: "preview", pc, over: {} };
  drawOverlay();
  void loadMasterState(pc, host.badge.company, st.site, () => onThisPage() && drawOverlay());
}

/**
 * A letter typed in the tag, the room description or Rename. Saved as you type, with no redraw, so the box keeps
 * its focus and the next tap lands where it was aimed. Undo takes back the whole box in one step.
 */
function typed(t: HTMLInputElement): void {
  if (!st) return;
  const s = st;
  const what = t.dataset.flIn;
  if (what === "rename") {
    if (s.sheet?.kind !== "rename") return;
    s.sheet.value = t.value;
    const f = gearById(s.sheet.gearId);
    const problem = f ? renameProblem(f.r, f.g.id, t.value) : "That gear is gone.";
    const line = host?.root.querySelector<HTMLElement>("#fl-renline");
    if (line) line.innerHTML = problem ? `<div class="ac-line red">${esc(problem)}</div>` : "";
    const go = host?.root.querySelector<HTMLButtonElement>("#fl-renamego");
    if (go) go.disabled = problem !== null;
    return;
  }
  const key = `${what}:${t.dataset.g ?? t.dataset.r ?? ""}`;
  if (s.typing !== key) {
    s.history.push(snap());
    if (s.history.length > UNDO_STEPS) s.history.shift();
    s.typing = key;
    s.notice = "";
  }
  if (what === "tag") {
    const f = gearById(t.dataset.g ?? "");
    if (!f) return;
    const v = cleanText(t.value, GEAR_TAG_MAX);
    if (v) f.g.tag = v;
    else delete f.g.tag;
  } else if (what === "desc") {
    const r = s.data.rooms.find((x) => x.name === t.dataset.r);
    if (!r) return;
    const v = cleanText(t.value, ROOM_DESCRIPTION_MAX);
    if (v) r.description = v;
    else delete r.description;
    const where = host?.root.querySelector<HTMLElement>("[data-fl-where]");
    if (where) where.innerHTML = roomWhere(r.description);
  } else return;
  writeSaved();
  const undoBtn = host?.root.querySelector<HTMLButtonElement>("#fl-undo");
  if (undoBtn) undoBtn.disabled = !s.history.length;
}

// ---- Events ----

function wire(): void {
  if (wired || !host) return;
  wired = true;
  // The print card's Find box.
  host.root.addEventListener("input", (ev) => {
    if (!onThisPage() || !st) return;
    const t = ev.target as HTMLInputElement;
    if (t.dataset.flIn) return typed(t);
    if (st.sheet?.kind !== "preview") return;
    cardFind(t, st.sheet.pc, drawOverlay, host!.root);
  });
  // Return on the iPad keyboard renames, the same as the Rename button.
  host.root.addEventListener("keydown", (ev) => {
    const t = ev.target as HTMLInputElement;
    if (!onThisPage() || ev.key !== "Enter" || t.dataset?.flIn !== "rename") return;
    ev.preventDefault();
    host?.root.querySelector<HTMLButtonElement>("#fl-renamego:not([disabled])")?.click();
  });
  host.root.addEventListener("focusout", (ev) => {
    if (st && (ev.target as HTMLElement).dataset?.flIn) st.typing = "";
    if (!repaintAfterTyping) return;
    setTimeout(() => {
      if (!repaintAfterTyping || !host || !onThisPage() || typingHere(host)) return;
      repaintAfterTyping = false;
      drawFieldLabels(host);
    }, 400);
  });
  host.root.addEventListener("click", (ev) => {
    if (!onThisPage() || !st) return;
    const target = ev.target as HTMLElement;
    const pc = target.closest<HTMLElement>("[data-pc]");
    if (pc && st.sheet?.kind === "preview") {
      if (!(pc as HTMLButtonElement).disabled) void fieldCardClick(pc);
      return;
    }
    const el = target.closest<HTMLElement>("[data-fl]");
    if (!el) {
      // A tap on the dark area around a sheet closes it, unless a PDF is being made.
      // Rooms with changes in it stays open: pick Cancel or Done, so a stray tap never keeps or loses them.
      const roomsDirty = (st.sheet?.kind === "rooms" || st.sheet?.kind === "rmroom") && (st.roomsStart?.steps ?? 0) > 0;
      if (target.matches("[data-fl-sheet], [data-pc-sheet]") && !roomsDirty && !(st.sheet?.kind === "preview" && st.sheet.pc.busy)) {
        if (st.sheet?.kind === "rmroom") {
          st.sheet = { kind: "rooms", sel: st.sheet.name };
          drawOverlay();
          return;
        }
        if (st.sheet?.kind === "rooms") st.roomsStart = null;
        if (st.sheet?.kind === "preview") {
          lastPrintRoom = st.sheet.pc.room;
          stopMasterWatches();
        }
        st.sheet = null;
        drawOverlay();
      }
      return;
    }
    if ((el as HTMLButtonElement).disabled) return;
    const s = st;
    const r = room();
    // Any other tap takes back an open Are You Sure? on Remove.
    if (s.rmAsk && !["rmyes", "rmno", "rmgear"].includes(el.dataset.fl ?? "")) s.rmAsk = null;
    if (s.unlockAsk && !["unlock", "unlockyes", "unlockno"].includes(el.dataset.fl ?? "")) s.unlockAsk = null;
    switch (el.dataset.fl) {
      case "close":
        s.menu = null;
        // Cancel on Remove ER7? goes back to Rooms, where you were. It never closes Rooms with it.
        if (s.sheet?.kind === "rmroom") s.sheet = { kind: "rooms", sel: s.sheet.name };
        else if (!(s.sheet?.kind === "preview" && s.sheet.pc.busy)) s.sheet = null;
        drawOverlay();
        break;
      case "undo":
        undo();
        break;
      case "sizes":
        s.menu = null;
        s.sheet = { kind: "sizes" };
        drawOverlay();
        break;
      case "preview":
        s.menu = null;
        // The room picked last stays picked while the page is open: print a room, hang it, come back for the next.
        // Print At picks start clean every time the card opens: they are for one print only.
        openCard();
        break;
      case "reprintgear":
        s.menu = null;
        openCard(el.dataset.g ?? "");
        break;
      case "goto":
        goTo(el.dataset.key ?? "");
        break;
      case "blocksize":
        printAt(el.dataset.from ?? "", el.dataset.size ?? "");
        break;
      case "startover":
      case "startovercancel":
        if (s.sheet?.kind === "preview") s.sheet.startOver = el.dataset.fl === "startover";
        drawOverlay();
        break;
      case "startoverclear": {
        // Not an edit: Undo never brings the printed record back, the same as printing.
        if (s.sheet?.kind !== "preview") break;
        s.data = startPrintingOver(s.data);
        s.sheet.startOver = false;
        s.sheet.pc.picks.clear();
        s.sheet.pc.seeded.clear();
        s.sheet.pc.small.clear();
        writeSaved();
        drawOverlay();
        break;
      }
      case "sizesreset": {
        // Back to the default for every block: this print's picks, and any size kept on the gear.
        const only = s.sheet?.kind === "preview" ? s.sheet.pc.room : "";
        if (s.sheet?.kind === "preview") {
          s.sheet.pc.small.clear();
          s.sheet.over = {};
        }
        if (!s.data.rooms.some((r) => (!only || r.name === only) && r.gear.some((g) => g.size || g.kidSize))) {
          drawOverlay();
          break;
        }
        change((x) => {
          for (const r of x.data.rooms) if (!only || r.name === only) for (const g of r.gear) {
            g.size = "";
            g.kidSize = "";
          }
        });
        break;
      }

      // Rooms
      case "rooms":
        openRooms();
        break;
      case "roomscancel":
        cancelRooms();
        break;
      case "go": {
        // A tap picks the room; Done goes there. Its code shows ready to change (B3). Tap it again to add rooms.
        const name = el.dataset.r ?? null;
        if (s.sheet?.kind === "rooms") {
          const again = s.sheet.sel === name && s.recode !== null;
          s.sheet.sel = name;
          s.recode = again ? null : recodeOf(name);
        }
        drawOverlay();
        break;
      }
      case "recodego": {
        const from = s.sheet?.kind === "rooms" ? s.sheet.sel : null;
        const rc = s.recode;
        if (!from || !rc) break;
        const to = `${rc.kind}${rc.num}${rc.part}`;
        if (recodeProblem(s.data, from, to) !== null) break;
        // No second window: Cancel and Undo take it back.
        change((x) => {
          if (recodeRoom(x.data, from, to) && x.cur === from) x.cur = to;
        });
        s.sheet = { kind: "rooms", sel: to };
        s.recode = recodeOf(to);
        drawOverlay();
        break;
      }
      case "roomsdone": {
        const sel = s.sheet?.kind === "rooms" ? s.sheet.sel : null;
        s.sheet = null;
        s.roomsStart = null;
        if (sel && sel !== s.cur && s.data.rooms.some((r) => r.name === sel)) {
          s.cur = sel;
          s.open = null;
          s.ordering = false;
          writeSaved();
          host?.root.querySelector("#fl-right")?.scrollTo({ top: 0 });
        }
        drawParts();
        break;
      }
      case "kind":
        if (s.recode) s.recode.kind = el.dataset.k === "EA" ? "EA" : "ER";
        else s.pick = { kind: (el.dataset.k as RoomKindRev2) ?? "ER", nums: new Set(), part: s.pick.part };
        drawOverlay();
        break;
      case "num": {
        const n = Number(el.dataset.n);
        if (s.recode) s.recode.num = n;
        else if (s.pick.nums.has(n)) s.pick.nums.delete(n);
        else s.pick.nums.add(n);
        drawOverlay();
        break;
      }
      case "part":
        if (s.recode) {
          s.recode.part = el.dataset.p ?? "";
          drawOverlay();
          break;
        }
        s.pick.part = el.dataset.p ?? "";
        s.pick.nums = new Set([...s.pick.nums].filter((n) => !s.data.rooms.some((x) => x.name === `${s.pick.kind}${n}${s.pick.part}`)));
        drawOverlay();
        break;
      case "addrooms": {
        const names = roomNames(s.pick.kind, [...s.pick.nums], s.pick.part);
        let first: string | null = null;
        change((x) => {
          const added = addRooms(x.data, names);
          first = added[0] ?? null;
          if (!room() && added[0]) {
            x.cur = added[0];
            x.open = null;
          }
        });
        s.pick.nums = new Set();
        s.recode = null;
        // The first room just added is the one picked, so Done goes there (walk item 18).
        s.sheet = { kind: "rooms", sel: first ?? (s.sheet?.kind === "rooms" ? s.sheet.sel : null) ?? s.cur };
        drawOverlay();
        break;
      }
      case "rmroom": {
        const sel = s.sheet?.kind === "rooms" ? s.sheet.sel : s.cur;
        if (!sel) break;
        s.sheet = { kind: "rmroom", name: sel };
        drawOverlay();
        break;
      }
      case "rmroomgo": {
        const gone = s.sheet?.kind === "rmroom" ? s.sheet.name : null;
        if (!gone) break;
        change((x) => {
          x.data.rooms = x.data.rooms.filter((y) => y.name !== gone);
          if (x.cur === gone) {
            x.cur = x.data.rooms[0]?.name ?? null;
            x.open = null;
          }
        });
        // Back to the Rooms sheet: you were working there.
        s.sheet = { kind: "rooms", sel: s.cur };
        drawOverlay();
        break;
      }
      case "roomdone":
        // Item 5: a tap marks the room Done, a tap again takes it back. It never moves you. It gates the push.
        change((x) => {
          const rr = x.data.rooms.find((y) => y.name === x.cur);
          if (!rr) return;
          if (rr.done) delete rr.done;
          else rr.done = true;
        });
        break;

      // Gear
      case "add": {
        if (!r) break;
        const type = el.dataset.type ?? "ENCL";
        // Tap PNL right after a panel: new panel or subpanel of it (decision 2). Anything else comes straight in.
        const offer = subpanelOffer(r, type);
        if (offer) {
          s.sheet = { kind: "panelpick", parentId: offer.parent.id };
          drawOverlay();
          break;
        }
        s.sheet = null;
        s.ordering = false;
        // Adding the next type closes the last one: one piece open at a time.
        change((x) => (x.open = addGear(r, type).id));
        break;
      }
      case "addplain":
        if (!r) break;
        s.sheet = null;
        s.ordering = false;
        change((x) => (x.open = addGear(r, "PNL").id));
        break;
      case "addsub": {
        const pid = s.sheet?.kind === "panelpick" ? s.sheet.parentId : "";
        s.sheet = null;
        s.ordering = false;
        if (r && pid) change((x) => (x.open = addSubpanel(r, pid)?.id ?? x.open));
        else drawOverlay();
        break;
      }

      // Order (F4a)
      case "order":
        startOrder();
        break;
      case "ordercancel":
        cancelOrder();
        break;
      case "orderdone":
        s.ordering = false;
        drawParts();
        break;

      // Rename (item 21)
      case "rename": {
        const f = gearById(el.dataset.g ?? "");
        s.menu = null;
        if (!f || f.g.type === "MCC") {
          drawOverlay();
          break;
        }
        if (lockedGear(f.g.id)) {
          s.notice = lockedWords([{ gearId: f.g.id }]);
          drawParts();
          break;
        }
        s.sheet = { kind: "rename", gearId: f.g.id, value: "" };
        drawOverlay();
        host?.root.querySelector<HTMLInputElement>('[data-fl-in="rename"]')?.focus();
        break;
      }
      case "renamego": {
        const sh = s.sheet;
        if (sh?.kind !== "rename") break;
        const f = gearById(sh.gearId);
        s.sheet = null;
        if (f && renameProblem(f.r, f.g.id, sh.value) === null) change(() => renameGear(f.r, f.g.id, sh.value));
        else drawOverlay();
        break;
      }
      case "more":
        s.sheet = { kind: "types" };
        drawOverlay();
        break;
      case "open":
        s.open = el.dataset.g ?? null;
        s.ordering = false;
        drawParts();
        break;
      case "done":
        s.open = null;
        drawParts();
        break;
      case "lab": {
        const f = gearById(el.dataset.g ?? "");
        if (!f) break;
        s.menu = { kind: "lab", gearId: f.g.id, anchor: el.getBoundingClientRect() };
        drawOverlay();
        break;
      }
      case "size":
        pickSize("size", el.dataset.size ?? "");
        break;
      case "kidsize":
        pickSize("kidSize", el.dataset.size ?? "");
        break;
      case "rmgear": {
        // Remove asks first, in the card's heading (Robert, 2026-10-28 4:43 PM; was one tap and Undo, 10-23).
        // From the label menu too: the card opens with the question.
        const f = gearById(el.dataset.g ?? "");
        s.menu = null;
        if (f && lockedGear(f.g.id)) {
          s.open = f.g.id;
          s.notice = lockedWords([{ gearId: f.g.id }]);
          drawParts();
        } else if (f) {
          s.open = f.g.id;
          s.rmAsk = f.g.id;
          drawParts();
        } else drawOverlay();
        break;
      }
      case "unlock": {
        const f = gearById(el.dataset.g ?? "");
        s.unlockAsk = f ? f.g.id : null;
        drawParts();
        break;
      }
      case "unlockno":
        s.unlockAsk = null;
        drawParts();
        break;
      case "unlockyes": {
        const f = gearById(el.dataset.g ?? "");
        s.unlockAsk = null;
        if (f) {
          s.unlocked.add(f.g.id);
          s.notice = "";
        }
        drawParts();
        break;
      }
      case "rmno":
        s.rmAsk = null;
        drawParts();
        break;
      case "rmyes": {
        const f = gearById(el.dataset.g ?? "");
        s.rmAsk = null;
        if (f) change((x) => {
          removeGear(f.r, f.g.id);
          x.open = null;
        });
        else drawParts();
        break;
      }

      // Sections
      case "sq":
        tapSquare(el);
        break;
      case "end": {
        const f = menuGear();
        if (f?.m.kind === "sq") {
          const m = f.m;
          change(() => endSectionAt(f.g, m.sec, m.space));
        }
        break;
      }
      case "split": {
        const f = menuGear();
        if (f?.m.kind === "sq") {
          const m = f.m;
          change(() => setSplit(f.g, m.sec, m.space, Number(el.dataset.n)));
        }
        break;
      }
      case "spare": {
        const f = menuGear();
        if (f?.m.kind === "sq") {
          const m = f.m;
          change(() => toggleSpare(f.g, m.sec, m.space));
        }
        break;
      }
      case "clearsec": {
        const f = menuGear();
        if (f?.m.kind === "sq") {
          const m = f.m;
          change(() => clearSection(f.g, m.sec));
        }
        break;
      }

      // Panels
      case "cc":
        tapCircuit(el);
        break;
      case "poles": {
        const f = menuGear();
        if (f?.m.kind === "cc") {
          const m = f.m;
          change(() => setPoles(f.g, m.top, Number(el.dataset.p)));
        }
        break;
      }
      case "gridmain":
      case "notmain": {
        // Main Breaker in the breaker's menu (MAIN_IN_GRID_B3_WORDS.md). No second window; Undo takes it back.
        const f = menuGear();
        if (f?.m.kind === "cc") {
          const m = f.m;
          change(() => (el.dataset.fl === "gridmain" ? setGridMain(f.g, m.top) : notTheMain(f.g)));
        }
        break;
      }
      case "clearbreaker": {
        const f = menuGear();
        if (f?.m.kind === "cc") {
          const m = f.m;
          change(() => clearBreaker(f.g, m.top));
        }
        break;
      }
      case "psize": {
        const f = gearById(el.dataset.g ?? "");
        if (f) change(() => setPanelSize(f.g, Number(el.dataset.n)));
        break;
      }
      case "main": {
        const f = gearById(el.dataset.g ?? "");
        if (f) change(() => toggleMain(f.g));
        break;
      }
      case "shockonly": {
        const f = gearById(el.dataset.g ?? "");
        if (f) change(() => toggleShockOnly(f.g));
        break;
      }
      case "photo": {
        // No photo yet: the camera opens. With one: full size, with Retake and Close.
        s.menu = null;
        const roomName = el.dataset.r ?? "";
        const gearId = el.dataset.g ?? "";
        const t = photoTarget(roomName, gearId);
        if (!t) break;
        if (!t.photo) {
          shootPhoto(roomName, gearId);
          break;
        }
        s.sheet = { kind: "photo", room: roomName, gearId };
        drawOverlay();
        break;
      }
      case "push": {
        // Push the room You Are In (items 1 to 3). Room Done is the gate; ER1 On Sheet has nothing to send.
        s.menu = null;
        const v = pushView();
        if (!v?.room) break;
        if (!v.done) {
          s.notice = `Mark ${v.room} Done first. The Room Done button is beside Order.`;
          s.good = "";
          drawParts();
          break;
        }
        if (v.fresh === 0 && !v.remove && !v.restore) {
          s.notice = "";
          s.good = `${v.room} is on the Assessment Sheet. Nothing new to push.`;
          drawParts();
          break;
        }
        location.hash = `#/assets/${s.site}/push/${encodeURIComponent(v.room)}`;
        break;
      }
      case "photoretake": {
        // The full-size sheet closes first: the camera opens over the page, never under a sheet.
        if (s.sheet?.kind !== "photo") break;
        const { room: roomName, gearId } = s.sheet;
        s.sheet = null;
        drawOverlay();
        shootPhoto(roomName, gearId);
        break;
      }
      case "brklabels": {
        const f = gearById(el.dataset.g ?? "");
        if (f)
          change(() => {
            if (f.g.breakerLabels) delete f.g.breakerLabels;
            else f.g.breakerLabels = true;
          });
        break;
      }
      case "clearbk": {
        const f = gearById(el.dataset.g ?? "");
        if (f)
          change(() => {
            f.g.circuits = {};
            f.g.main = false;
            delete f.g.blanks;
            delete f.g.mainAt;
            delete f.g.mainCircuit;
            delete f.g.parked;
          });
        break;
      }
      case "mainat": {
        const f = gearById(el.dataset.g ?? "");
        if (f)
          change(() => {
            if (f.g.mainAt === "bottom") delete f.g.mainAt;
            else f.g.mainAt = "bottom";
          });
        break;
      }
      case "tandem": {
        const f = menuGear();
        if (f?.m.kind === "cc") {
          const m = f.m;
          change(() => toggleTandem(f.g, m.top));
        }
        break;
      }
      case "blank": {
        const f = menuGear();
        if (f?.m.kind === "cc") {
          const m = f.m;
          change(() => setBlank(f.g, m.top));
        }
        break;
      }
      case "unblank": {
        const f = menuGear();
        if (f?.m.kind === "bl") {
          const m = f.m;
          change(() => unBlank(f.g, m.n, el.dataset.b === "1"));
        }
        break;
      }
      case "sechd": {
        const f = gearById(el.dataset.g ?? "");
        if (!f) break;
        s.open = f.g.id;
        s.menu = { kind: "sh", gearId: f.g.id, sec: Number(el.dataset.s), anchor: el.getBoundingClientRect() };
        drawOverlay();
        break;
      }
      case "covers": {
        // The menu stays open: set top and bottom in one go.
        const f = menuGear();
        if (f?.m.kind === "sh") {
          const m = f.m;
          change(() => setCovers(f.g, m.sec, el.dataset.w === "bottom" ? "bottom" : "top", Number(el.dataset.n)));
          s.menu = m;
          drawOverlay();
        }
        break;
      }
    }
  });
  window.addEventListener("resize", () => {
    if (onThisPage()) fitHeight();
  });
}
